import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSelector, useDispatch } from 'react-redux'
import {
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  StarIcon,
  FireIcon,
  GiftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  HeartIcon,
  EyeIcon,
  CurrencyDollarIcon,
  PaintBrushIcon,
  PhotoIcon,
  BoltIcon,
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  TruckIcon,
  ClockIcon,
  MapPinIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline'
import {
  StarIcon as StarSolidIcon,
  HeartIcon as HeartSolidIcon,
  SparklesIcon as SparklesSolidIcon,
  FireIcon as FireSolidIcon
} from '@heroicons/react/24/solid'
import type { RootState } from '../store'
import { 
  addToCart, 
  removeFromCart, 
  updateCartQuantity, 
  clearCart,
  createOrder,
  spendCoins
} from '../store/slices/gamificationSlice'
import type { ShopItem } from '../store/slices/gamificationSlice'
import toast from 'react-hot-toast'

// Local type definitions
type AvailabilityStatus = 'available' | 'out_of_stock' | 'coming_soon'

type ExtendedShopItem = ShopItem & {
  availability: AvailabilityStatus
}

// Category icons mapping
const categoryIcons = {
  'Elektronik': BoltIcon,
  'Kitaplar': PhotoIcon,
  'Kırtasiye': PaintBrushIcon,
  'Hediye Kartları': GiftIcon,
  'Aksesuar': StarIcon
}

// Rarity configuration
const rarityConfig: Record<ShopItem['rarity'], {
  color: string
  glow: string
  border: string
  text: string
  bg: string
}> = {
  common: {
    color: 'from-gray-400 to-gray-600',
    glow: 'shadow-gray-500/20',
    border: 'border-gray-400/30',
    text: 'text-gray-600',
    bg: 'bg-gray-50 dark:bg-gray-800/50'
  },
  rare: {
    color: 'from-blue-400 to-blue-600',
    glow: 'shadow-blue-500/30',
    border: 'border-blue-400/40',
    text: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20'
  },
  epic: {
    color: 'from-purple-400 to-purple-600',
    glow: 'shadow-purple-500/40',
    border: 'border-purple-400/50',
    text: 'text-purple-600',
    bg: 'bg-purple-50 dark:bg-purple-900/20'
  },
  legendary: {
    color: 'from-yellow-400 to-orange-500',
    glow: 'shadow-yellow-500/50',
    border: 'border-yellow-400/60',
    text: 'text-yellow-600',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20'
  }
}

// Extended shop items with real products
const extendedShopItems: ExtendedShopItem[] = [
  // Electronics
  {
    id: 'wireless_headphones',
    name: 'Kablosuz Kulaklık',
    description: 'Yüksek kaliteli ses deneyimi için Bluetooth kulaklık',
    type: 'electronics' as const,
    price: 2500,
    category: 'Elektronik',
    rarity: 'rare' as const,
    preview: '🎧',
    owned: false,
    equipped: false,
    isNew: true,
    isPopular: true,
    isPhysical: true,
    deliveryType: 'shipping' as const,
    estimatedDelivery: '3-5 iş günü',
    image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop',
    originalPrice: 3000,
    discount: 17,
    availability: 'available' as const
  },
  {
    id: 'smartphone',
    name: 'Akıllı Telefon',
    description: 'Son teknoloji özelliklerle donatılmış akıllı telefon',
    type: 'electronics' as const,
    price: 15000,
    category: 'Elektronik',
    rarity: 'legendary' as const,
    preview: '📱',
    owned: false,
    equipped: false,
    isNew: true,
    isPopular: true,
    isPhysical: true,
    deliveryType: 'shipping' as const,
    estimatedDelivery: '1-2 iş günü',
    image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=300&h=300&fit=crop',
    originalPrice: 18000,
    discount: 17,
    availability: 'out_of_stock' as const
  },
  // Books
  {
    id: 'programming_book',
    name: 'JavaScript Programlama Kitabı',
    description: 'Modern web geliştirme için kapsamlı JavaScript rehberi',
    type: 'book' as const,
    price: 450,
    category: 'Kitaplar',
    rarity: 'rare' as const,
    preview: '📚',
    owned: false,
    equipped: false,
    isNew: true,
    isPopular: true,
    isPhysical: true,
    deliveryType: 'shipping' as const,
    estimatedDelivery: '2-3 iş günü',
    image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=300&h=300&fit=crop',
    originalPrice: 500,
    discount: 10,
    availability: 'coming_soon' as const
  },
  // Gift Cards
  {
    id: 'amazon_gift_card',
    name: 'Amazon Hediye Kartı',
    description: '100 TL değerinde Amazon alışveriş kartı - Anında teslim',
    type: 'gift_card' as const,
    price: 1800,
    category: 'Hediye Kartları',
    rarity: 'epic' as const,
    preview: '🎁',
    owned: false,
    equipped: false,
    isNew: true,
    isPopular: true,
    isPhysical: false,
    deliveryType: 'instant' as const,
    estimatedDelivery: 'Anında',
    image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=300&h=300&fit=crop',
    originalPrice: 2000,
    discount: 10,
    availability: 'available' as const
  },
  {
    id: 'steam_gift_card',
    name: 'Steam Hediye Kartı',
    description: '50 TL değerinde Steam oyun kartı - Anında teslim',
    type: 'gift_card' as const,
    price: 900,
    category: 'Hediye Kartları',
    rarity: 'rare' as const,
    preview: '🎮',
    owned: false,
    equipped: false,
    isNew: false,
    isPopular: true,
    isPhysical: false,
    deliveryType: 'instant' as const,
    estimatedDelivery: 'Anında',
    image: 'https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=300&h=300&fit=crop',
    originalPrice: 1000,
    discount: 10,
    availability: 'available' as const
  },
  // Stationery
  {
    id: 'premium_notebook',
    name: 'Premium Defter Seti',
    description: 'Kaliteli kağıt ve şık tasarımlı 3lü defter seti',
    type: 'stationery' as const,
    price: 180,
    category: 'Kırtasiye',
    rarity: 'rare' as const,
    preview: '📓',
    owned: false,
    equipped: false,
    isNew: false,
    isPopular: true,
    isPhysical: true,
    deliveryType: 'shipping' as const,
    estimatedDelivery: '2-4 iş günü',
    image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=300&h=300&fit=crop',
    originalPrice: 220,
    discount: 18,
    availability: 'out_of_stock' as const
  }
]

interface ShippingAddress {
  fullName: string
  address: string
  city: string
  postalCode: string
  phone: string
}

// Sepet ve siparişlerde kullanılan minimal tip yardımcıları
// Yalnızca bu bileşende kullanılan alanları kapsar; global slice'dan bağımsızdır
export type CartItemWithDetails = {
  id: string
  quantity: number
  addedAt?: string
  item: {
    id: string
    name: string
    price: number
    preview: string
    image?: string
    deliveryType: 'instant' | 'shipping'
  }
}

export type OrderType = {
  id: string
  items: {
    itemId: string
    quantity: number
    price: number
    name: string
    image?: string
    deliveryType: 'instant' | 'shipping'
  }[]
  totalAmount: number
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  orderDate: string
  shippingAddress?: ShippingAddress
  trackingNumber?: string
  deliveryCode?: string
}

const Store: React.FC = () => {
  const dispatch = useDispatch()
  const { coins, cart, orders } = useSelector((state: RootState) => state.gamification)
  
  const [selectedCategory, setSelectedCategory] = useState<string>('Tümü')
  type RarityType = 'Tümü' | 'common' | 'rare' | 'epic' | 'legendary'
  const [selectedRarity, setSelectedRarity] = useState<RarityType>('Tümü')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'rarity'>('name')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ExtendedShopItem | null>(null)
  const [favorites, setFavorites] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showCart, setShowCart] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: '',
    address: '',
    city: '',
    postalCode: '',
    phone: ''
  })
  const [showOrders, setShowOrders] = useState(false)

  // Filter and sort items
  const filteredItems: ExtendedShopItem[] = useMemo(() => {
    let filtered = extendedShopItems

    // Filter by category
    if (selectedCategory !== 'Tümü') {
      filtered = filtered.filter(item => item.category === selectedCategory)
    }

    // Filter by rarity
    if (selectedRarity !== 'Tümü') {
      filtered = filtered.filter(item => item.rarity === selectedRarity)
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Sort items
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price
        case 'rarity':
          const rarityOrder: Record<ShopItem['rarity'], number> = { common: 0, rare: 1, epic: 2, legendary: 3 }
          return rarityOrder[b.rarity] - rarityOrder[a.rarity]
        default:
          return a.name.localeCompare(b.name)
      }
    })

    return filtered
  }, [selectedCategory, selectedRarity, searchQuery, sortBy])

  const categories = ['Tümü', ...Array.from(new Set(extendedShopItems.map(item => item.category)))]
  const rarities: RarityType[] = ['Tümü', 'common', 'rare', 'epic', 'legendary'] as const

  // Cart calculations
  const cartItems = cart.map(cartItem => {
    const item = extendedShopItems.find(shopItem => shopItem.id === cartItem.id)
    return { ...cartItem, item }
  }).filter(cartItem => cartItem.item) as unknown as CartItemWithDetails[]

  const cartTotal = cartItems.reduce((total, cartItem) => {
    return total + (cartItem.item.price * cartItem.quantity)
  }, 0)

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0)

  const handleAddToCart = (item: ShopItem) => {
    dispatch(addToCart({ itemId: item.id, quantity: 1 }))
    toast.success(`${item.name} sepete eklendi!`)
  }

  const handleRemoveFromCart = (itemId: string) => {
    dispatch(removeFromCart(itemId))
    toast.success('Ürün sepetten çıkarıldı!')
  }

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    dispatch(updateCartQuantity({ itemId, quantity }))
  }

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Sepetiniz boş!')
      return
    }

    if (coins < cartTotal) {
      toast.error('Yetersiz coin!')
      return
    }

    // Check if there are physical items that need shipping
    const hasPhysicalItems = cartItems.some(cartItem => cartItem.item!.deliveryType === 'shipping')
    
    if (hasPhysicalItems) {
      setShowCart(false)
      setShowCheckout(true)
    } else {
      // Only virtual items, process immediately
      processOrder()
    }
  }

  const processOrder = (address?: ShippingAddress) => {
    const orderItems = cartItems.map(cartItem => ({
      itemId: cartItem.item!.id,
      name: cartItem.item!.name,
      price: cartItem.item!.price,
      quantity: cartItem.quantity,
      image: cartItem.item!.image,
      deliveryType: cartItem.item!.deliveryType
    }))

    dispatch(createOrder({
      items: orderItems,
      totalAmount: cartTotal,
      deliveryDate: address ? new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString() : undefined,
      shippingAddress: address,
      trackingNumber: address ? 'TRK-' + Math.random().toString(36).substr(2, 8).toUpperCase() : undefined,
      deliveryCode: !address ? 'GIFT-' + Math.random().toString(36).substr(2, 8).toUpperCase() : undefined
    }))

    dispatch(spendCoins(cartTotal))
    dispatch(clearCart())
    
    setShowCheckout(false)
    setShippingAddress({ fullName: '', address: '', city: '', postalCode: '', phone: '' })
    
    if (address) {
      toast.success('Siparişiniz alındı! Kargo bilgileri e-posta ile gönderilecek.')
    } else {
      toast.success('Siparişiniz tamamlandı! Kodlarınız anında teslim edildi.')
    }
  }

  const toggleFavorite = (itemId: string) => {
    setFavorites(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-purple-400/20 rounded-full animate-pulse" />
          <div className="absolute top-32 right-20 w-16 h-16 bg-pink-400/20 rounded-full animate-bounce" />
          <div className="absolute bottom-20 left-1/3 w-12 h-12 bg-blue-400/20 rounded-full animate-ping" />
        </div>
        
        <div className="relative px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-6"
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity }
              }}
            >
              <ShoppingBagIcon className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Mağaza
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
              Gerçek ürünleri puanlarınızla satın alın! 🛍️
            </p>
            
            {/* Coin Display and Action Buttons */}
            <div className="flex items-center justify-center gap-4">
              <motion.div
                className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <CurrencyDollarIcon className="w-6 h-6 text-yellow-500" />
                <span className="text-2xl font-bold text-gray-800 dark:text-white">
                  {coins.toLocaleString()}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">Coin</span>
              </motion.div>
              
              {/* Cart Button */}
              <motion.button
                onClick={() => setShowCart(true)}
                className="relative inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ShoppingCartIcon className="w-6 h-6 text-purple-500" />
                <span className="text-lg font-semibold text-gray-800 dark:text-white">Sepet</span>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </motion.button>
              
              {/* Orders Button */}
              <motion.button
                onClick={() => setShowOrders(true)}
                className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <TruckIcon className="w-6 h-6 text-blue-500" />
                <span className="text-lg font-semibold text-gray-800 dark:text-white">Siparişler</span>
                {orders.length > 0 && (
                  <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ml-1">
                    {orders.length}
                  </span>
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Ürün ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            
            {/* Filters Toggle */}
            <motion.button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-white dark:hover:bg-gray-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>Filtreler</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </motion.button>
          </div>
          
          {/* Filter Options */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8 overflow-hidden"
              >
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Category Filter */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Kategori</label>
                      <div className="space-y-2">
                        {categories.map(category => {
                          const IconComponent = category !== 'Tümü' ? categoryIcons[category as keyof typeof categoryIcons] : SparklesIcon
                          return (
                            <motion.button
                              key={category}
                              onClick={() => setSelectedCategory(category)}
                              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                                selectedCategory === category
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-2 border-purple-300'
                                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <IconComponent className="w-5 h-5" />
                              <span className="font-medium">{category}</span>
                            </motion.button>
                          )
                        })}
                      </div>
                    </div>
                    
                    {/* Rarity Filter */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Nadir</label>
                      <div className="space-y-2">
                        {rarities.map(rarity => (
                          <motion.button
                            key={rarity}
                            onClick={() => setSelectedRarity(rarity)}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                              selectedRarity === rarity
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-2 border-purple-300'
                                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className={`w-3 h-3 rounded-full ${
                              rarity === 'Tümü' ? 'bg-gray-400' :
                              rarity === 'common' ? 'bg-gray-400' :
                              rarity === 'rare' ? 'bg-blue-400' :
                              rarity === 'epic' ? 'bg-purple-400' :
                              'bg-yellow-400'
                            }`} />
                            <span className="font-medium">
                              {rarity === 'Tümü' ? 'Tümü' :
                               rarity === 'common' ? 'Yaygın' :
                               rarity === 'rare' ? 'Nadir' :
                               rarity === 'epic' ? 'Epik' : 'Efsanevi'}
                            </span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Sort Options */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Sırala</label>
                      <div className="space-y-2">
                        {[
                          { value: 'name', label: 'İsme Göre', icon: SparklesIcon },
                          { value: 'price', label: 'Fiyata Göre', icon: CurrencyDollarIcon },
                          { value: 'rarity', label: 'Nadirliğe Göre', icon: StarIcon }
                        ].map(option => (
                          <motion.button
                            key={option.value}
                            onClick={() => setSortBy(option.value as 'name' | 'price' | 'rarity')}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                              sortBy === option.value
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-2 border-purple-300'
                                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <option.icon className="w-5 h-5" />
                            <span className="font-medium">{option.label}</span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 dark:text-gray-400">
              <span className="font-semibold text-purple-600">{filteredItems.length}</span> ürün bulundu
            </p>
          </div>
          
          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item, index) => {
              const isFavorite = favorites.includes(item.id)
              const rarity = rarityConfig[item.rarity]
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`group relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border-2 ${rarity.border} hover:shadow-2xl ${rarity.glow} transition-all duration-300 hover:-translate-y-2`}
                  whileHover={{ scale: 1.02 }}
                >
                  {/* Availability Overlay */}
                  {item.availability === 'out_of_stock' && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center z-20">
                      <div className="text-center">
                        <div className="bg-red-500 text-white px-4 py-2 rounded-xl font-bold text-lg mb-2">
                          TÜKENDİ
                        </div>
                        <p className="text-white text-sm">Stokta kalmadı</p>
                      </div>
                    </div>
                  )}
                  
                  {item.availability === 'coming_soon' && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center z-20">
                      <div className="text-center">
                        <div className="bg-blue-500 text-white px-4 py-2 rounded-xl font-bold text-lg mb-2">
                          YAKINDA
                        </div>
                        <p className="text-white text-sm">Çok yakında satışta</p>
                      </div>
                    </div>
                  )}

                  {/* New/Popular Badges */}
                  <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                    {item.isNew && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                        <SparklesSolidIcon className="w-3 h-3" />
                        YENİ
                      </span>
                    )}
                    {item.isPopular && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                        <FireSolidIcon className="w-3 h-3" />
                        POPÜLER
                      </span>
                    )}
                    {item.discount && (
                      <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        %{item.discount} İNDİRİM
                      </span>
                    )}
                  </div>
                  
                  {/* Delivery Type Badge */}
                  <div className="absolute top-4 right-4 z-10">
                    {item.deliveryType === 'instant' ? (
                      <div className="bg-green-500 text-white text-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        ANINDA
                      </div>
                    ) : (
                      <div className="bg-blue-500 text-white text-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                        <TruckIcon className="w-3 h-3" />
                        KARGO
                      </div>
                    )}
                  </div>

                  {/* Favorite Button */}
                  <button
                    onClick={() => toggleFavorite(item.id)}
                    className="absolute top-4 right-16 z-10 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-transform"
                  >
                    {isFavorite ? (
                      <HeartSolidIcon className="w-5 h-5 text-red-500" />
                    ) : (
                      <HeartIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Product Image */}
                  <div className="relative mb-4 overflow-hidden rounded-xl">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x300?text=' + encodeURIComponent(item.preview || item.name)
                      }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${rarity.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
                  </div>
                  
                  {/* Product Info */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${rarity.bg} ${rarity.text}`}>
                        {item.rarity === 'common' ? 'Yaygın' :
                         item.rarity === 'rare' ? 'Nadir' :
                         item.rarity === 'epic' ? 'Epik' : 'Efsanevi'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.category}</span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-purple-600 transition-colors">
                      {item.name}
                    </h3>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {item.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.discount && item.originalPrice ? (
                          <>
                            <span className="text-xl font-bold text-gray-800 dark:text-white">
                              {item.price.toLocaleString()} Coin
                            </span>
                            <span className="text-sm text-gray-500 line-through">
                              {item.originalPrice.toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <span className="text-xl font-bold text-gray-800 dark:text-white">
                            {item.price.toLocaleString()} Coin
                          </span>
                        )}
                        <span className="text-sm text-yellow-600 font-semibold">Coin</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <motion.button
                        onClick={() => handleAddToCart(item)}
                        className={`flex-1 py-2 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                          item.availability === 'out_of_stock' || item.availability === 'coming_soon'
                            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            : coins < item.price
                            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                        }`}
                        whileHover={item.availability === 'available' && coins >= item.price ? { scale: 1.02 } : {}}
                        whileTap={item.availability === 'available' && coins >= item.price ? { scale: 0.98 } : {}}
                        disabled={coins < item.price || item.availability !== 'available'}
                      >
                        <ShoppingCartIcon className="w-4 h-4" />
                        {item.availability === 'out_of_stock' ? 'Tükendi' : 
                         item.availability === 'coming_soon' ? 'Yakında' : 'Sepete Ekle'}
                      </motion.button>
                      
                      <motion.button
                        onClick={() => setSelectedItem(item)}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <ShoppingBagIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aradığınız kriterlere uygun ürün bulunamadı</p>
              <p className="text-gray-400">Farklı filtreler deneyebilirsiniz</p>
            </div>
          )}
        </div>
      </div>

      {/* Cart Modal */}
      <AnimatePresence>
        {showCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCart(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <ShoppingCartIcon className="w-6 h-6 text-purple-500" />
                  Sepetim ({cartCount} ürün)
                </h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Sepetiniz boş</p>
                  <p className="text-gray-400">Ürün eklemek için mağazaya göz atın</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cartItems.map((cartItem) => (
                      <div key={cartItem.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                        <img
                          src={cartItem.item!.image}
                          alt={cartItem.item!.name}
                          className="w-16 h-16 object-cover rounded-lg"
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = 'https://via.placeholder.com/64x64?text=' + encodeURIComponent(cartItem.item!.preview)
                          }}
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 dark:text-white">{cartItem.item!.name}</h3>
                          <p className="text-sm text-gray-500">{cartItem.item!.price.toLocaleString()} Coin</p>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            {cartItem.item!.deliveryType === 'instant' ? (
                              <><ClockIcon className="w-3 h-3" /> Anında teslim</>
                            ) : (
                              <><TruckIcon className="w-3 h-3" /> Kargo teslimi</>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateQuantity(cartItem.id, cartItem.quantity - 1)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                            disabled={cartItem.quantity <= 1}
                          >
                            <MinusIcon className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-semibold">{cartItem.quantity}</span>
                          <button
                            onClick={() => handleUpdateQuantity(cartItem.id, cartItem.quantity + 1)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemoveFromCart(cartItem.id)}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-semibold text-gray-800 dark:text-white">Toplam:</span>
                      <span className="text-2xl font-bold text-purple-600">{cartTotal.toLocaleString()} Coin</span>
                    </div>
                    
                    <button
                      onClick={handleCheckout}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={coins < cartTotal}
                    >
                      {coins < cartTotal ? 'Yetersiz Coin' : 'Satın Al'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCheckout(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <CreditCardIcon className="w-6 h-6 text-purple-500" />
                  Sipariş Tamamla
                </h2>
                <button
                  onClick={() => setShowCheckout(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5" />
                    Teslimat Adresi
                  </h3>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Fiziksel ürünleriniz için teslimat adresi bilgilerinizi girin
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      value={shippingAddress.fullName}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, fullName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Şehir
                    </label>
                    <input
                      type="text"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                      placeholder="Şehir"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adres
                  </label>
                  <textarea
                    value={shippingAddress.address}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, address: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    placeholder="Detaylı adres bilgisi"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Posta Kodu
                    </label>
                    <input
                      type="text"
                      value={shippingAddress.postalCode}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                      placeholder="Posta kodu"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                      placeholder="Telefon numaranız"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-semibold text-gray-800 dark:text-white">Toplam:</span>
                    <span className="text-2xl font-bold text-purple-600">{cartTotal.toLocaleString()} Coin</span>
                  </div>
                  
                  <button
                    onClick={() => {
                      if (!shippingAddress.fullName || !shippingAddress.address || !shippingAddress.city || !shippingAddress.postalCode || !shippingAddress.phone) {
                        toast.error('Lütfen tüm alanları doldurun!')
                        return
                      }
                      processOrder(shippingAddress)
                    }}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                  >
                    Siparişi Tamamla
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Orders Modal */}
      <AnimatePresence>
        {showOrders && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowOrders(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <TruckIcon className="w-6 h-6 text-purple-500" />
                  Siparişlerim ({orders.length})
                </h2>
                <button
                  onClick={() => setShowOrders(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <TruckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Henüz siparişiniz yok</p>
                  <p className="text-gray-400">İlk siparişinizi vermek için mağazaya göz atın</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-800 dark:text-white">Sipariş #{order.id.slice(-8)}</h3>
                          <p className="text-sm text-gray-500">{new Date(order.orderDate).toLocaleDateString('tr-TR')}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {order.status === 'pending' ? 'Beklemede' :
                             order.status === 'processing' ? 'Hazırlanıyor' :
                             order.status === 'shipped' ? 'Kargoda' :
                             order.status === 'delivered' ? 'Teslim Edildi' :
                             'İptal Edildi'}
                          </span>
                          <p className="text-lg font-bold text-purple-600 mt-1">{order.totalAmount.toLocaleString()} Coin</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded"
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.src = 'https://via.placeholder.com/48x48?text=📦'
                              }}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 dark:text-white">{item.name}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Adet: {item.quantity}</span>
                                <span>{item.price.toLocaleString()} Coin</span>
                                <div className="flex items-center gap-1">
                                  {item.deliveryType === 'instant' ? (
                                    <><ClockIcon className="w-3 h-3" /> Anında teslim</>
                                  ) : (
                                    <><TruckIcon className="w-3 h-3" /> Kargo</>
                                  )}
                                </div>
                              </div>
                            </div>
                            {item.deliveryType === 'instant' && order.status === 'pending' && (
                              <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                                Kod: {order.deliveryCode || 'GIFT-' + Math.random().toString(36).substr(2, 8).toUpperCase()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {order.shippingAddress && (
                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                             <MapPinIcon className="w-4 h-4" />
                             Teslimat Adresi
                           </h4>
                           <p className="text-sm text-gray-600 dark:text-gray-300">
                             {order.shippingAddress.fullName}<br />
                             {order.shippingAddress.address}<br />
                             {order.shippingAddress.city} {order.shippingAddress.postalCode}<br />
                             {order.shippingAddress.phone}
                           </p>
                         </div>
                       )}

                       {order.trackingNumber && (
                         <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                           <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-1">Kargo Takip</h4>
                           <p className="text-sm text-blue-600 dark:text-blue-400">Takip No: {order.trackingNumber}</p>
                         </div>
                       )}
                     </div>
                   ))}
                 </div>
               )}
             </motion.div>
           </motion.div>
         )}
       </AnimatePresence>

       {/* Product Detail Modal */}
       <AnimatePresence>
         {selectedItem && (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setSelectedItem(null)}
           >
             <motion.div
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               exit={{ scale: 0.9, opacity: 0 }}
               className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
             >
               <div className="flex items-center justify-between mb-6">
                 <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Ürün Detayı</h2>
                 <button
                   onClick={() => setSelectedItem(null)}
                   className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                 >
                   <XMarkIcon className="w-6 h-6 text-gray-500" />
                 </button>
               </div>

               <div className="grid md:grid-cols-2 gap-6">
                 <div>
                   <img
                     src={selectedItem.image}
                     alt={selectedItem.name}
                     className="w-full h-64 object-cover rounded-xl"
                     onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                       e.currentTarget.src = 'https://via.placeholder.com/400x400?text=' + encodeURIComponent(selectedItem.preview ?? selectedItem.name)
                     }}
                   />
                 </div>
                 
                 <div className="space-y-4">
                   <div>
                     <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{selectedItem.name}</h3>
                     <p className="text-gray-600 dark:text-gray-300">{selectedItem.description}</p>
                   </div>

                   <div className="flex items-center gap-4">
                     <span className={`px-3 py-1 rounded-full text-sm font-semibold ${rarityConfig[selectedItem.rarity].bg} ${rarityConfig[selectedItem.rarity].text}`}>
                       {selectedItem.rarity === 'common' ? 'Yaygın' :
                        selectedItem.rarity === 'rare' ? 'Nadir' :
                        selectedItem.rarity === 'epic' ? 'Epik' : 'Efsanevi'}
                     </span>
                     
                     {selectedItem.deliveryType === 'instant' ? (
                       <div className="bg-green-500 text-white text-sm px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                         <ClockIcon className="w-4 h-4" />
                         Anında Teslim
                       </div>
                     ) : (
                       <div className="bg-blue-500 text-white text-sm px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                         <TruckIcon className="w-4 h-4" />
                         Kargo Teslimi
                       </div>
                     )}
                   </div>

                   <div className="flex items-center gap-4">
                     <div className="flex items-center gap-2">
                       {selectedItem.discount && selectedItem.originalPrice ? (
                         <>
                           <span className="text-2xl font-bold text-gray-800 dark:text-white">
                             {selectedItem.price.toLocaleString()} Coin
                           </span>
                           <span className="text-lg text-gray-500 line-through">
                             {selectedItem.originalPrice.toLocaleString()}
                           </span>
                         </>
                       ) : (
                         <span className="text-2xl font-bold text-gray-800 dark:text-white">
                           {selectedItem.price.toLocaleString()} Coin
                         </span>
                       )}
                     </div>
                   </div>

                   <div className="flex items-center gap-2 text-sm text-gray-500">
                     {selectedItem.deliveryType === 'instant' ? (
                       <ClockIcon className="w-4 h-4" />
                     ) : (
                       <TruckIcon className="w-4 h-4" />
                     )}
                     <span>Teslimat: {selectedItem.estimatedDelivery}</span>
                   </div>

                   <div className="flex gap-3 pt-4">
                     <motion.button
                       onClick={() => {
                         handleAddToCart(selectedItem)
                         setSelectedItem(null)
                       }}
                       className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center justify-center gap-2"
                       whileHover={{ scale: 1.02 }}
                       whileTap={{ scale: 0.98 }}
                       disabled={coins < selectedItem.price}
                     >
                       <ShoppingCartIcon className="w-5 h-5" />
                       Sepete Ekle
                     </motion.button>
                   </div>
                 </div>
               </div>
             </motion.div>
           </motion.div>
         )}
       </AnimatePresence>
     </div>
   )
 }

 export default Store
